<script setup lang="ts">
/**
 * Ícones desenhados aqui, e não trazidos de uma biblioteca.
 *
 * São 25 traçados; uma dependência de ícones custaria centenas de kB para
 * usar 25. Todos com o mesmo traço de 1.5 e a mesma grade de 24 — mistura
 * de espessuras é o que mais denuncia interface montada às pressas.
 *
 * Nenhum emoji: emoji muda de forma por sistema operacional, não aceita
 * `currentColor` e não escala com o texto.
 */
const props = withDefaults(
  defineProps<{ nome: string; tamanho?: number; rotulo?: string }>(),
  { tamanho: 18, rotulo: undefined },
)

const tracados: Record<string, string> = {
  painel: 'M3 13h8V3H3zM13 21h8V11h-8zM13 7h8V3h-8zM3 21h8v-4H3z',
  contrato: 'M8 3h8l4 4v14H4V3zM15 3v5h5M8 13h8M8 17h5',
  fornecedor: 'M3 21h18M5 21V8l7-5 7 5v13M9 21v-5h6v5',
  pessoas:
    'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75',
  chave: 'M14 7a4 4 0 1 1 3.9 5L14 12l-2 2-2-2-2 2-2-2-3 3v-3l7-7z',
  engrenagem:
    'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z',
  alerta:
    'M12 9v4M12 17h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0',
  relogio: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20M12 6v6l4 2',
  arquivo: 'M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9zM13 2v7h7',
  calculadora:
    'M6 2h12a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2M8 6h8v4H8zM8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01M16 18h.01',
  historico: 'M3 3v5h5M3.05 13A9 9 0 1 0 6 5.3L3 8M12 7v5l4 2',
  busca: 'M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16M21 21l-4.35-4.35',
  filtro: 'M22 3H2l8 9.46V19l4 2v-8.54z',
  mais: 'M12 5v14M5 12h14',
  seta: 'M5 12h14M12 5l7 7-7 7',
  // Aponta para baixo (aberto). Fechado, quem gira é o CSS — um traçado só.
  chevron: 'M6 9l6 6 6-6',
  voltar: 'M19 12H5M12 19l-7-7 7-7',
  confere: 'M20 6 9 17l-5-5',
  fechar: 'M18 6 6 18M6 6l12 12',
  baixar: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3',
  enviar: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12',
  externo: 'M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14 21 3',
  empresa:
    'M3 21h18M5 21V5a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v16M15 21V11h4a2 2 0 0 1 2 2v8M9 7h2M9 11h2M9 15h2',
  moeda: 'M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6',
  saida: 'M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9',
  menu: 'M3 6h18M3 12h18M3 18h18',
}
</script>

<template>
  <svg
    class="icone"
    :width="props.tamanho"
    :height="props.tamanho"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    stroke-width="1.5"
    stroke-linecap="round"
    stroke-linejoin="round"
    :aria-hidden="props.rotulo ? undefined : 'true'"
    :aria-label="props.rotulo"
    :role="props.rotulo ? 'img' : undefined"
  >
    <path :d="tracados[props.nome] ?? tracados.contrato" />
  </svg>
</template>

<style scoped>
.icone {
  flex: none;
  /* Alinha o ícone à linha de base do texto ao lado. */
  vertical-align: -0.125em;
}
</style>
